import {
  BankRemittanceReceiptFormFields
} from "@/features/invoices/bank-remittance-receipts/upload-bank-remittance-receipt-page";
import { Field, FieldProps, FormikHelpers, FormikProps } from "formik";
import React, { useCallback } from "react";
import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import { FormControl, FormLabel, Grid, Skeleton } from "@chakra-ui/react";
import { toTitle } from "@/utils/case";
import { Company } from "@/features/types";
import AppDatePicker from "@/features/line-sheet-sets/app-date-picker";
import AppInput from "@/features/ui/app-input";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useIdentity from "@/features/ui/hooks/use-identity";
import useDisclosureWithData from "@/features/order-sheet-sets/hooks/use-disclosure-with-data";
import CreateCompanyModal, { CreateCompanyModalProps } from "@/features/ui/create-company-modal";
import { InvoiceUtils } from "@/features/invoices/invoice-utils";

interface FormFields extends BankRemittanceReceiptFormFields {}

interface BankRemittanceReceiptInformationFormControlModalProps {
  formik: FormikProps<FormFields>;
  isEditing?: boolean;
}

export default function BankRemittanceReceiptInformationFormControlModal({
  formik,
  isEditing = false,
}: BankRemittanceReceiptInformationFormControlModalProps) {
  const identity = useIdentity();
  const { t, tTitle } = useI18nHelper();
  type LINK_AFFECTING_KEYS =
    | "season"
    | "orderedBy"
    | "issuedThrough"
    | "sender"
    | "receiver";

  const handleLinkAffectingFieldChange = useCallback(
    <T extends LINK_AFFECTING_KEYS>(
      key: LINK_AFFECTING_KEYS,
      value: FormFields[T],
      setValues: FormikHelpers<FormFields>["setValues"]
    ) => {
      return setValues((prev) => {
        let next = {
          ...prev,
          [key]: value,
          payments: [],
        };

        const currency = InvoiceUtils.getCurrencyByParties(next);
        next.amount = {
          value: next.amount?.value,
          currency,
        };

        next.bankRemittanceReceiptDetailList =
          next.bankRemittanceReceiptDetailList.map((detail) => {
            return {
              ...detail,
              amount: {
                value: detail.amount?.value,
                currency,
              },
            };
          });

        // if (key === "issuedThrough") {
        //   if (next.sender?.types === "AGENCY") {
        //     // @ts-ignore
        //     next.sender = value;
        //   }
        //
        //   if (next.receiver?.types === "AGENCY") {
        //     // @ts-ignore
        //     next.receiver = value;
        //   }
        // }

        return next;
      });
    },
    []
  );

  const {
    isOpen: isCreateCompanyModelOpen,
    onClose: onCreateCompanyModalClose,
    data: createCompanyModalData,
    openWithData: openCreateCompanyModal,
  } = useDisclosureWithData<CreateCompanyModalProps>();

  if (!formik.values.id) {
    return (
      <AppPanel title={<Skeleton height={"32px"} />}>
        <Grid
          rowGap={"8px"}
          gridTemplateRows={"repeat(7, 16px 24px) repeat(2, 16px 60px)"}
        >
          {(Array(18).fill(0) as number[]).map((_, index) => {
            return <Skeleton key={index} />;
          })}
        </Grid>
      </AppPanel>
    );
  }

  return (
    <AppPanel title={tTitle("bank_remittance_receipt_information")}>
      <Grid rowGap={"8px"}>
        <Field name={"issuedOn"}>
          {(props: FieldProps<Date, FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                isRequired={isEditing}
                gap={"4px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("remittance_date"))}
                </FormLabel>
                <AppDatePicker
                  width={"100%"}
                  showTimeInput={false}
                  name={toTitle(t("remittance_date"))}
                  value={props.field.value}
                  isReadOnly={!isEditing}
                  onChange={(value) => {
                    props.form.setFieldValue(props.field.name, value);
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        <Field name={"number"}>
          {(props: FieldProps<FormFields["number"], FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                gap={"4px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  거래번호 (Ref No)
                </FormLabel>
                <AppInput
                  isReadOnly={!isEditing}
                  value={props.field.value ?? ""}
                  onChange={(value) => {
                    props.form.setFieldValue(props.field.name, value);
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        <Field name={"sender"}>
          {(props: FieldProps<FormFields["sender"], FormFields>) => {
            const options: AppSelectOption<Company>[] = [];
            //송금하는 주체, 에 or 바
            if (props.form.values.issuedThrough) {
              if (props.form.values.receiver?.type !== "AGENCY") {
                options.push({
                  name: props.form.values.issuedThrough.name,
                  value: {
                    ...props.form.values.issuedThrough,
                    type: "AGENCY",
                  },
                });
              }
            }

            if (props.form.values.orderedBy) {
              options.push({
                name: props.form.values.orderedBy.name,
                value: {
                  ...props.form.values.orderedBy,
                  type: "BUYER",
                },
              });
            }

            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                gap={"4px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("sender"))}
                </FormLabel>
                {props.field.value?.name}
              </FormControl>
            );
          }}
        </Field>

        <Field name={"receiver"}>
          {(props: FieldProps<FormFields["receiver"], FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                gap={"4px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("receiver"))}
                </FormLabel>
                {props.field.value?.name}
              </FormControl>
            );
          }}
        </Field>
      </Grid>
      {isCreateCompanyModelOpen && createCompanyModalData && (
        <CreateCompanyModal
          isOpen={isCreateCompanyModelOpen}
          onClose={onCreateCompanyModalClose}
          {...createCompanyModalData}
        />
      )}
    </AppPanel>
  );
}
